import { FC, ReactNode } from "react";
import { Collapse, CollapseProps } from "antd";
import { useTranslation } from "react-i18next";
import { TbArmchair, TbArrowRight, TbCopy, TbLuggage, TbToolsKitchen2 } from "react-icons/tb";
import i18n from "src/i18n";
import { useToast } from "src/context/ToastContext";
import type { Flight, Passenger, Segment } from "src/Modules/Flights/constants/JourneyResult";
import type { BaggageAddOn, MealAddOn, SeatAddOn } from "src/store/slices/client/client.api";
import SSRChips from "../SSRChips";

export const EMPTY_VALUE = "-";

type AddOn = SeatAddOn | MealAddOn | BaggageAddOn;

export type SSRConfig<T extends AddOn = any> = {
  label: string;
  chipLabel: string;
  icon: ReactNode;
  keyInBackend: "seat" | "meal" | "baggage";
  count: (items: T[]) => number;
  transform: (items: T[]) => string;
};

export type FormulatedSSR = {
  label: string;
  chipLabel: string;
  icon: ReactNode;
  count: number;
  segments: { flights: { flight: Flight; value: string }[] }[];
};

const getSeatLabel = (seat: SeatAddOn) =>
  seat.rowNo && !seat.seatNo?.startsWith(seat.rowNo) ? `${seat.rowNo}${seat.seatNo}` : seat.seatNo || seat.code;

const getMealLabel = (meal: MealAddOn) => meal.airlineDescription || meal.description || meal.code;

export const SSR_MAP: Record<string, SSRConfig> = {
  seat: {
    label: "BookingResult.ticketDetails.columns.seat",
    chipLabel: "BookingResult.ticketDetails.seats",
    icon: <TbArmchair className="size-4" />,
    keyInBackend: "seat",
    count: (seats: SeatAddOn[]) => seats.length,
    transform: (seats: SeatAddOn[]) => seats.map(getSeatLabel).join(", "),
  } satisfies SSRConfig<SeatAddOn>,
  meal: {
    label: "BookingResult.ticketDetails.columns.meal",
    chipLabel: "BookingResult.ticketDetails.meals",
    icon: <TbToolsKitchen2 className="size-4" />,
    keyInBackend: "meal",
    count: (meals: MealAddOn[]) => meals.reduce((total, meal) => total + meal.quantity, 0),
    transform: (meals: MealAddOn[]) =>
      meals.map((meal) => (meal.quantity > 1 ? `${meal.quantity} x ${getMealLabel(meal)}` : getMealLabel(meal))).join(", "),
  } satisfies SSRConfig<MealAddOn>,
  baggage: {
    label: "BookingResult.ticketDetails.columns.extraBaggage",
    chipLabel: "BookingResult.ticketDetails.extraBags",
    icon: <TbLuggage className="size-4" />,
    keyInBackend: "baggage",
    count: (bags: BaggageAddOn[]) => bags.length,
    transform: (bags: BaggageAddOn[]) => {
      const byWeight = new Map<number, number>();
      bags.forEach((bag) => byWeight.set(bag.weight, (byWeight.get(bag.weight) ?? 0) + 1));
      return Array.from(byWeight, ([weight, count]) => i18n.t("BookingResult.ticketDetails.baggageCount", { count, weight })).join(", ");
    },
  } satisfies SSRConfig<BaggageAddOn>,
};

const NO_SELECTION_CODES = ["NoMeal", "NoBaggage"];

const isSameFlight = (addOn: AddOn, flight: Flight) =>
  addOn.flightNumber === flight.flightNumber && addOn.origin === flight.origin.code && addOn.destination === flight.destination.code;

export const formulateSSRs = (passenger: Passenger, segments: Segment[], map: Record<string, SSRConfig>): Record<string, FormulatedSSR> =>
  Object.fromEntries(
    Object.entries(map).map(([key, config]) => {
      const items = ((passenger[config.keyInBackend] as AddOn[] | null) ?? []).filter((item) => !NO_SELECTION_CODES.includes(item.code));
      return [
        key,
        {
          label: config.label,
          chipLabel: config.chipLabel,
          icon: config.icon,
          count: config.count(items),
          segments: segments.map((segment) => ({
            flights: segment.flights.map((flight) => ({
              flight,
              value: config.transform(items.filter((item) => isSameFlight(item, flight))) || EMPTY_VALUE,
            })),
          })),
        },
      ];
    }),
  );

const getInitials = (passenger: Passenger) => `${passenger.firstName?.[0] ?? ""}${passenger.lastName?.[0] ?? ""}`;

const PASSENGER_TYPE_TITLES = ["CHD", "INF"];

const getFullName = (passenger: Passenger) => {
  const title = PASSENGER_TYPE_TITLES.includes(passenger.title?.toUpperCase()) ? null : passenger.title;
  return [title, passenger.firstName, passenger.lastName].filter(Boolean).join(" ");
};

const PassengerHeader: FC<{ passenger: Passenger; ssrs: Record<string, FormulatedSSR> }> = ({ passenger, ssrs }) => {
  const { t } = useTranslation();
  const toast = useToast();

  const handleCopy = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(passenger.ticketNumber ?? "");
    toast.success(t("UserHistory.giftcard.copiedToClipboard"));
  };

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-16">
        <div className="flex min-w-0 items-center gap-3">
          <div className="bg-background-primary-lighter text-font-active flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold uppercase">
            {getInitials(passenger)}
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="text-font-default truncate text-sm font-semibold">{getFullName(passenger)}</span>
            <span className="text-font-subtle text-xs">
              {t(`Flights.details.passengerType.${passenger.type?.toLowerCase()}`, { defaultValue: passenger.type })}
              {passenger.isLeadPax && ` · ${t("BookingResult.passengerDetails.primaryContact")}`}
            </span>
          </div>
        </div>
        <div className="flex flex-col">
          <span className="text-font-subtle text-xs uppercase">{t("BookingResult.ticketDetails.ticketNumber")}</span>
          <div className="text-font-default flex items-center gap-2 font-mono text-sm font-semibold" dir="ltr">
            {passenger.ticketNumber || EMPTY_VALUE}
            {passenger.ticketNumber && (
              <button
                type="button"
                aria-label={t("BookingResult.ticketDetails.copyTicketNumber")}
                onClick={handleCopy}
                className="text-font-subtle cursor-pointer"
              >
                <TbCopy className="size-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      <SSRChips ssrs={Object.values(ssrs)} />
    </div>
  );
};

const SSRTable: FC<{ segments: Segment[]; ssrs: Record<string, FormulatedSSR> }> = ({ segments, ssrs }) => {
  const { t } = useTranslation();
  const columns = Object.values(ssrs);

  return (
    <div className="flex flex-col">
      <div className="bg-background-minimal border-border-subtle hidden grid-cols-4 gap-4 border-b px-4 py-3 md:grid">
        <span className="text-font-subtle text-xs font-medium">{t("BookingResult.ticketDetails.columns.flight")}</span>
        {columns.map((ssr) => (
          <span key={ssr.label} className="text-font-subtle text-xs font-medium">
            {t(ssr.label)}
          </span>
        ))}
      </div>
      <div className="divide-border-subtle flex flex-col divide-y">
        {segments.map((segment, segmentIndex) =>
          segment.flights.map((flight, flightIndex) => (
            <div key={`${segmentIndex}-${flightIndex}`} className="grid grid-cols-3 gap-x-4 gap-y-3 px-4 py-3 md:grid-cols-4">
              <div className="col-span-3 flex flex-col md:col-span-1">
                <span className="text-font-default flex items-center gap-1 text-sm font-semibold" dir="ltr">
                  {flight.origin.code}
                  <TbArrowRight className="size-3.5" />
                  {flight.destination.code}
                </span>
                <span className="text-font-subtle text-xs">
                  {t(`BookingResult.ticketDetails.${segment.direction === "Inbound" ? "return" : "departure"}`)} ·{" "}
                  <span dir="ltr">{`${flight.airlineCode} ${flight.flightNumber}`}</span>
                </span>
              </div>
              {columns.map((ssr) => (
                <div key={ssr.label} className="flex min-w-0 flex-col gap-1">
                  <span className="text-font-subtle text-xs md:hidden">{t(ssr.label)}</span>
                  <span className="text-font-default text-sm">{ssr.segments[segmentIndex].flights[flightIndex].value}</span>
                </div>
              ))}
            </div>
          )),
        )}
      </div>
    </div>
  );
};

const transformPassengerToData = (passengers: Passenger[], segments: Segment[]): CollapseProps["items"] =>
  passengers.map((passenger, index) => {
    const ssrs = formulateSSRs(passenger, segments, SSR_MAP);
    return {
      key: passenger.paxId ?? index,
      label: <PassengerHeader passenger={passenger} ssrs={ssrs} />,
      children: <SSRTable segments={segments} ssrs={ssrs} />,
    };
  });

const PassengerData: FC<{ passengers: Passenger[]; segments: Segment[] }> = ({ passengers, segments }) => {
  const items = transformPassengerToData(passengers, segments);

  return (
    <Collapse
      accordion={true}
      items={items}
      defaultActiveKey={passengers[0]?.paxId ?? 0}
      expandIconPlacement="end"
      classNames={{
        header: "bg-background-default! rounded-sm!",
      }}
    />
  );
};

export default PassengerData;
